import { useQuery } from '@apollo/client';
import { useState } from 'react';
import ButtonComponent from '../../../components/UI/Button/Button.component';
import { VERDICT_TEMPLATES } from '../../../graphql/resolvers/moderation/verdictTemplates.gql';
import '../moderation.scss';

interface Template {
  key: string;
  verdict: 'DISMISS' | 'REWRITE' | 'REMOVE';
  reportReason: string | null;
  headline: string;
  body: string;
}

interface Props {
  verdict: 'DISMISS' | 'REWRITE' | 'REMOVE';
  reason: string | null;
  onConfirm: (templateKey: string) => Promise<void>;
  onCancel: () => void;
}

export default function VerdictTemplatePicker({
  verdict,
  reason,
  onConfirm,
  onCancel,
}: Props) {
  const { data, loading, error } = useQuery(VERDICT_TEMPLATES, {
    fetchPolicy: 'cache-first',
  });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (loading)
    return (
      <div className="Moderation__Picker__Loading">Loading templates…</div>
    );
  if (error)
    return (
      <div className="Moderation__Picker__Error">Failed to load templates.</div>
    );

  const templates: Template[] = (data?.verdictTemplates ?? []).filter(
    (t: Template) =>
      t.verdict === verdict &&
      (t.reportReason === null || t.reportReason === reason),
  );

  const handleConfirm = async () => {
    if (!selectedKey) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm(selectedKey);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="Moderation__Picker" data-testid="verdict-template-picker">
      {templates.map((t) => (
        <button
          type="button"
          key={t.key}
          className={`Moderation__Template ${selectedKey === t.key ? 'is-selected' : ''}`}
          onClick={() => setSelectedKey(t.key)}
        >
          <strong>{t.headline}</strong>
          <p>{t.body}</p>
        </button>
      ))}
      {submitError && (
        <div className="Moderation__Picker__Error">{submitError}</div>
      )}
      <div className="Moderation__Picker__Actions">
        <ButtonComponent
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </ButtonComponent>
        <ButtonComponent
          variant="primary"
          size="sm"
          onClick={handleConfirm}
          disabled={!selectedKey || submitting}
        >
          Confirm
        </ButtonComponent>
      </div>
    </div>
  );
}
